import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useAssignCertification } from "@/hooks/use-persons";

interface PersonCertificationAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
}

/**
 * Admin assigns a catalog certification to a person, optionally with an
 * initial status, expiry date, and notes. Proof upload happens later on the
 * dashboard row itself, not here — we want assignment to be one click.
 */
export function PersonCertificationAssignDialog({
  open,
  onOpenChange,
  personId,
}: PersonCertificationAssignDialogProps) {
  const assign = useAssignCertification(personId);

  const [certificationId, setCertificationId] = useState("");
  const [status, setStatus] = useState<"assigned" | "started" | "completed">("assigned");
  const [expiresAt, setExpiresAt] = useState("");
  const [notes, setNotes] = useState("");

  function reset() {
    setCertificationId("");
    setStatus("assigned");
    setExpiresAt("");
    setNotes("");
  }

  // Clear the form whenever the dialog closes (cancel, escape, backdrop) so a
  // reopen starts blank rather than showing the last abandoned input.
  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!certificationId) return;
    await assign.mutateAsync({
      certificationId,
      status,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
      notes: notes.trim() || null,
    });
    handleOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assign certification</DialogTitle>
          <DialogDescription>
            Pick a certification from the catalog and the initial lifecycle state.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="certification">Certification</Label>
            <EntityCombobox
              entity="certifications"
              value={certificationId || null}
              onChange={(v) => setCertificationId(v ?? "")}
              filters={{ status: "active" }}
              placeholder="Pick a certification"
              searchPlaceholder="Search certifications…"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="status">Initial status</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as "assigned" | "started" | "completed")}
            >
              <SelectTrigger id="status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="started">Started</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="expires">Expires (optional)</Label>
            <Input
              id="expires"
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any context worth recording"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!certificationId || assign.isPending}>
              {assign.isPending ? "Assigning…" : "Assign"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
